import { Link } from "react-router-dom";
import { Logo } from "@/components/comiq/Logo";
import { Eyebrow } from "@/components/comiq/Eyebrow";
import { Contour } from "@/components/comiq/Contour";
import { COLORS, CHART_COLORS, STATUS, RADII, TYPE_SCALE } from "@/comiq/tokens";
import { ArrowRight } from "lucide-react";

const Swatch = ({ name, hex, dark }) => (
  <div data-testid={`swatch-${name}`} className="flex flex-col">
    <div
      className="h-16 w-full rounded-comiq-sm border"
      style={{ background: hex, borderColor: "#E4DCCB" }}
    />
    <span className="mt-2 text-sm font-semibold" style={{ color: "#17140F" }}>{name}</span>
    <span className="text-xs font-mono" style={{ color: "#6A6256" }}>{hex}</span>
  </div>
);

const previews = [
  { to: "/preview/intro", tag: "Respondent", title: "Survey introduction", desc: "Kalem, jelas, selesai ± 11 menit — header ringkas, kerahasiaan, dan CTA." },
  { to: "/preview/question", tag: "Respondent", title: "Survey question page", desc: "Lebar baca nyaman, skala 1–5, opsi “tidak yakin”, progres & autosave." },
  { to: "/preview/admin", tag: "Research", title: "Admin dashboard", desc: "Tabel, filter, chart, dan label status — bukan halaman marketing." },
];

export default function PreviewHub() {
  return (
    <div className="min-h-screen" style={{ background: COLORS.paper }}>
      <header className="border-b" style={{ borderColor: COLORS.line }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Logo showTagline />
          <span className="text-xs font-medium" style={{ color: COLORS.textSoft }}>Design System · v1</span>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <Contour className="pointer-events-none absolute -right-10 -top-6 h-64 w-[420px] opacity-90" />
        <div className="mx-auto max-w-6xl px-6 pb-10 pt-14">
          <Eyebrow>ComIQ Survey &amp; Research Platform</Eyebrow>
          <h1 className="font-serif-editorial mt-4 text-4xl sm:text-5xl lg:text-6xl" style={{ color: COLORS.ink, maxWidth: 820 }}>
            A design system that feels like a <em>natural extension</em> of ComIQ.
          </h1>
          <p className="mt-5 max-w-2xl text-base" style={{ color: COLORS.textSoft }}>
            Warna, tipografi, tombol, kontrol, dan warna chart dibangun sebagai token bersama —
            diambil dan diverifikasi langsung dari comiq.id. Tinjau ringkasan sistem lalu tiga preview di bawah untuk persetujuan.
          </p>
        </div>
      </section>

      {/* Previews */}
      <section className="mx-auto max-w-6xl px-6 pb-14">
        <div className="grid gap-5 md:grid-cols-3">
          {previews.map((p) => (
            <Link
              key={p.to}
              to={p.to}
              data-testid={`preview-link-${p.title.split(" ")[1]}`}
              className="group flex flex-col rounded-comiq-lg border bg-comiq-surface-alt p-6 transition-all duration-200 hover:-translate-y-0.5"
              style={{ borderColor: COLORS.line }}
            >
              <Eyebrow>{p.tag}</Eyebrow>
              <h3 className="font-serif-editorial mt-3 text-2xl" style={{ color: COLORS.ink }}>{p.title}</h3>
              <p className="mt-2 flex-1 text-sm" style={{ color: COLORS.textSoft }}>{p.desc}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: COLORS.orangeInk }}>
                Lihat preview <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Tokens */}
      <section className="border-t" style={{ borderColor: COLORS.line, background: COLORS.surface }}>
        <div className="mx-auto max-w-6xl space-y-12 px-6 py-14">
          {/* Colors */}
          <div>
            <Eyebrow>Color tokens</Eyebrow>
            <h2 className="font-serif-editorial mt-2 text-3xl" style={{ color: COLORS.ink }}>Palet</h2>
            <div className="mt-6 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-6">
              <Swatch name="paper" hex={COLORS.paper} />
              <Swatch name="ink" hex={COLORS.ink} />
              <Swatch name="orange" hex={COLORS.orange} />
              <Swatch name="orange-ink" hex={COLORS.orangeInk} />
              <Swatch name="surface" hex={COLORS.surfaceAlt} />
              <Swatch name="line" hex={COLORS.line} />
            </div>
          </div>

          {/* Type */}
          <div>
            <Eyebrow>Typography</Eyebrow>
            <h2 className="font-serif-editorial mt-2 text-3xl" style={{ color: COLORS.ink }}>
              Inter untuk kontrol · Newsreader untuk judul
            </h2>
            <div className="mt-6 divide-y rounded-comiq border bg-comiq-surface-alt" style={{ borderColor: COLORS.line }}>
              {TYPE_SCALE.map((t) => (
                <div key={t.name} className="flex flex-col gap-1 px-5 py-4 sm:flex-row sm:items-baseline sm:justify-between">
                  <span className={t.cls} style={{ color: COLORS.ink }}>{t.sample}</span>
                  <span className="text-xs font-mono" style={{ color: COLORS.textSoft }}>{t.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Buttons + radii */}
          <div className="grid gap-8 md:grid-cols-2">
            <div>
              <Eyebrow>Buttons</Eyebrow>
              <h2 className="font-serif-editorial mt-2 text-3xl" style={{ color: COLORS.ink }}>Pil, seperti ComIQ</h2>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button className="btn-comiq btn-accent" data-testid="btn-demo-accent">Primary action</button>
                <button className="btn-comiq btn-line" data-testid="btn-demo-line">Secondary</button>
              </div>
              <p className="mt-3 text-sm" style={{ color: COLORS.textSoft }}>Orange hanya untuk aksi utama &amp; state terpilih.</p>
            </div>
            <div>
              <Eyebrow>Radii</Eyebrow>
              <h2 className="font-serif-editorial mt-2 text-3xl" style={{ color: COLORS.ink }}>Sudut</h2>
              <div className="mt-6 flex flex-wrap gap-4">
                {Object.entries(RADII).map(([k, v]) => (
                  <div key={k} className="flex flex-col items-center gap-2">
                    <div className="h-16 w-16 border bg-comiq-surface-alt" style={{ borderRadius: v, borderColor: COLORS.lineStrong }} />
                    <span className="text-xs font-mono" style={{ color: COLORS.textSoft }}>{k} · {v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Chart + status */}
          <div className="grid gap-8 md:grid-cols-2">
            <div>
              <Eyebrow>Chart colors</Eyebrow>
              <h2 className="font-serif-editorial mt-2 text-3xl" style={{ color: COLORS.ink }}>Seri data</h2>
              <div className="mt-6 flex gap-2">
                {CHART_COLORS.map((c) => (
                  <div key={c} className="flex-1">
                    <div className="h-14 rounded-comiq-sm" style={{ background: c }} />
                    <span className="mt-1 block text-[10px] font-mono" style={{ color: COLORS.textSoft }}>{c}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Eyebrow>Status &amp; data quality</Eyebrow>
              <h2 className="font-serif-editorial mt-2 text-3xl" style={{ color: COLORS.ink }}>Label</h2>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {Object.entries(STATUS).map(([k, s]) => (
                  <span key={k} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold" style={{ color: s.fg, background: s.bg }}>
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.fg }} />
                    {s.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t px-6 py-8 text-center text-xs" style={{ borderColor: COLORS.line, color: COLORS.textSoft }}>
        ComIQ Research Platform — design preview. Referensi: comiq.id · insights.
      </footer>
    </div>
  );
}
